import { useState, useEffect, useMemo } from 'react';
import Page from '../../../Components/Layout/Page';
import { AppBarTitle } from '../../../layout/appbar/Title/context';
import useApiQuery from '../../../Api/useApiQuery';
import { authenticatedApi } from '../../../Api/api';
import { useNavigate, useLocation } from 'react-router-dom';
import ITask from '../../../models/task';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import { isMobile } from 'react-device-detect';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import ModalUpdateTask from '../Sav/Pages/CalendarTaskList/ModalUpdateTask';
import { useDebounce } from 'usehooks-ts';

const now = new Date();

interface PageInfos {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: number;
  previousPage: number;
  totalCount: number;
  totalPages: number;
}

interface TasksResponse {
  nodes: ITask[];
  pageInfos: PageInfos;
}

interface TasksListProps {
  defaultTab?: string;
}

export default function TasksList({ defaultTab = 'calendar' }: TasksListProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalUpdateTask, setModalUpdateTask] = useState<ITask | null>(null);
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  
  // Paramètres de pagination
  const onLoadUrlParams = new URLSearchParams(window.location.search);
  const [page, setPage] = useState(parseInt(onLoadUrlParams.get('page') || '0', 10));
  const [search, setSearch] = useState(onLoadUrlParams.get('search') || '');
  const searchDebounced = useDebounce(search, 500);

  // Mettre à jour l'URL lorsqu'on change d'onglet
  useEffect(() => {
    if (activeTab !== defaultTab) {
      navigate(`/tasks/${activeTab}${location.search}`, { replace: true });
    }
  }, [activeTab, defaultTab, navigate, location.search]);
  
  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const lastDay = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59);

  const [firstDayOfCalendar, setFirstDayOfCalendar] = useState(firstDay);
  const [lastDayOfCalendar, setLastDayOfCalendar] = useState(lastDay);

  // Paramètres de requête pour la pagination
  const upcomingQueryParams = useMemo(() => {
    const params: any = {
      afterDateTime: now.toISOString(),
      beforeDateTime: lastDayOfCalendar.toISOString(),
      isDone: false,
      total: 20,
      page
    };
    if (searchDebounced) params.search = searchDebounced;
    return params;
  }, [lastDayOfCalendar, page, searchDebounced]);

  const overdueQueryParams = useMemo(() => {
    const params: any = {
      beforeDateTime: now.toISOString(),
      isDone: false,
      total: 20,
      page
    };
    if (searchDebounced) params.search = searchDebounced;
    return params;
  }, [page, searchDebounced]);
  
  // Paramètres de requête pour les tâches terminées
  const completedQueryParams = useMemo(() => {
    const params: any = {
      isDone: true,
      total: 20,
      page,
      sortBy: 'startDate',
      sortOrder: 'DESC'
    };
    if (searchDebounced) params.search = searchDebounced;
    return params;
  }, [page, searchDebounced]);

  const calendarQueryParams = useMemo(() => {
    return {
      afterDateTime: firstDayOfCalendar.toISOString(),
      beforeDateTime: lastDayOfCalendar.toISOString(),
      total: 100, // Récupérer plus de tâches pour le calendrier
      page: 0
    };
  }, [firstDayOfCalendar, lastDayOfCalendar]);

  // Mettre à jour l'URL avec les paramètres de pagination
  useEffect(() => {
    const urlParams = new URLSearchParams();
    if (page > 0) urlParams.set('page', page.toString());
    if (search) urlParams.set('search', search);
    const url = `${window.location.pathname}?${urlParams.toString()}`;
    navigate(url, { replace: true });
  }, [page, search, navigate]);

  // Récupérer les tâches à venir avec pagination
  const { data: upcomingTasksData, refetch: refetchUpcomingTasks } = useApiQuery<TasksResponse>('/tasks/v2', {
    params: upcomingQueryParams
  });

  // Récupérer les tâches en retard avec pagination
  const { data: overdueTasksData, refetch: refetchOverdueTasks } = useApiQuery<TasksResponse>('/tasks/v2', {
    params: overdueQueryParams
  });

  // Récupérer les tâches terminées avec pagination
  const { data: completedTasksData, refetch: refetchCompletedTasks } = useApiQuery<TasksResponse>('/tasks/v2', {
    params: completedQueryParams
  });

  // Récupérer toutes les tâches pour le calendrier
  const { data: allTasksData, refetch: refetchAllTasks } = useApiQuery<TasksResponse>('/tasks/v2', {
    params: calendarQueryParams
  });
  
  // Valeurs par défaut si les données ne sont pas encore chargées
  const upcomingTasks = upcomingTasksData || { nodes: [], pageInfos: { hasNextPage: false, hasPreviousPage: false, nextPage: 0, previousPage: 0, totalCount: 0, totalPages: 0 }};
  const overdueTasks = overdueTasksData || { nodes: [], pageInfos: { hasNextPage: false, hasPreviousPage: false, nextPage: 0, previousPage: 0, totalCount: 0, totalPages: 0 }};
  const completedTasks = completedTasksData || { nodes: [], pageInfos: { hasNextPage: false, hasPreviousPage: false, nextPage: 0, previousPage: 0, totalCount: 0, totalPages: 0 }};
  const allTasks = allTasksData?.nodes || [];

  const refetchAll = async () => {
    await Promise.all([
      refetchUpcomingTasks(),
      refetchOverdueTasks(),
      refetchCompletedTasks(),
      refetchAllTasks()
    ]);
  };

  const events = allTasks.map((task) => {
    const startDate = new Date(task.startDate);
    const color = task.isDone ? 'green' : (startDate.getTime() < now.getTime() ? 'red' : 'blue');

    return {
      id: task.id.toString(),
      title: task.title,
      start: task.startDate,
      end: task.endDate,
      allDay: false,
      backgroundColor: color,
      borderColor: color,
      extendedProps: {
        task,
      }
    }
  }) || [];

  const handleDateSelect = async (selectInfo: any) => {
    try {
      const title = prompt('Ajouter un titre à l\'événement : ');

      if (title) {
        await authenticatedApi.post('/tasks', {
          title,
          startDate: selectInfo.start,
          endDate: selectInfo.end,
        });
        await refetchAll();
      }
    } catch (error) {
      console.error(error);
    } finally {
      const calendarApi = selectInfo.view.calendar
      calendarApi.unselect()
    }
  }

  const handleEventClick = (clickInfo: any) => {
    setModalUpdateTask(clickInfo.event.extendedProps.task);
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleTaskDone = async (taskId: number, isDone: boolean) => {
    try {
      await authenticatedApi.patch(`/tasks/${taskId}`, {
        isDone
      });
      await refetchAll();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la tâche:', error);
    }
  };

  return (
    <Page>
      <AppBarTitle title="Tâches SAV" />

      {!!modalUpdateTask && (
        <ModalUpdateTask
          task={modalUpdateTask}
          onRequestClose={() => setModalUpdateTask(null)}
          onSaved={async () => {
            setModalUpdateTask(null);
            await refetchAll();
          }}
        />
      )}

      <div className="mb-3">
        <div className="input-group mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher une tâche..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </div>
      </div>
      
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => {
          const newTab = k || 'calendar';
          setActiveTab(newTab);
        }}
        className="mb-3"
      >
        <Tab eventKey="calendar" title="Calendrier">
          <div style={{ height: 'calc(100vh - 200px)' }}>
            <FullCalendar
              datesSet={(dateInfo: any) => {
                setFirstDayOfCalendar(dateInfo.start);
                setLastDayOfCalendar(dateInfo.end);
              }}
              locale={frLocale}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: isMobile ? 'timeGridWeek,timeGridDay' : 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              height={'100%'}
              displayEventTime={true}
              firstDay={1}
              initialView={isMobile ? 'timeGridWeek' : 'dayGridMonth'}
              editable={true}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              events={events}
              select={handleDateSelect}
              eventChange={async (changeInfo: any) => {
                await authenticatedApi.patch(`/tasks/${changeInfo.event.extendedProps.task.id}`, {
                  startDate: changeInfo.event.start?.toISOString(),
                  endDate: changeInfo.event.end?.toISOString(),
                });
                await refetchAll();
              }}
              eventClick={handleEventClick}
            />
          </div>
        </Tab>
        <Tab eventKey="overdue" title={`Tâches en retard (${overdueTasks.pageInfos.totalCount || 0})`}>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Description</th>
                  <th>Date prévue</th>
                  <th>Réf. contrat</th>
                  <th>Bénéficiaire</th>
                  <th>Garant</th>
                  <th>SAV</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {overdueTasks.nodes.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center">Aucune tâche en retard</td>
                  </tr>
                )}
                {overdueTasks.nodes.map((task) => (
                  <tr key={task.id} className="bg-danger bg-opacity-10">
                    <td>{task.title}</td>
                    <td>{task.description}</td>
                    <td>{formatDate(task.startDate)}</td>
                    <td>{task.sav?.contract?.label || '-'}</td>
                    <td>
                      {task.sav?.contract?.beneficiary 
                        ? `${task.sav.contract.beneficiary.firstName} ${task.sav.contract.beneficiary.lastName}`
                        : '-'}
                    </td>
                    <td>{task.sav?.contract?.company?.name || '-'}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-link"
                        onClick={() => navigate(`/sav/${task.savId}`)}
                      >
                        SAV #{task.savId}
                      </button>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => handleTaskDone(task.id, true)}
                        >
                          Marquer comme terminée
                        </button>
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => setModalUpdateTask(task)}
                        >
                          Modifier
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination pour les tâches en retard */}
          {overdueTasks.pageInfos.totalCount > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Page {page + 1} / {overdueTasks.pageInfos.totalPages || 1}
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-primary"
                  disabled={!overdueTasks.pageInfos.hasPreviousPage}
                  onClick={() => setPage(page - 1)}
                >
                  Précédent
                </button>
                <button
                  className="btn btn-outline-primary"
                  disabled={!overdueTasks.pageInfos.hasNextPage}
                  onClick={() => setPage(page + 1)}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </Tab>
        <Tab eventKey="upcoming" title={`Tâches à venir (${upcomingTasks.pageInfos.totalCount || 0})`}>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Description</th>
                  <th>Date prévue</th>
                  <th>Réf. contrat</th>
                  <th>Bénéficiaire</th>
                  <th>Garant</th>
                  <th>SAV</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcomingTasks.nodes.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center">Aucune tâche à venir</td>
                  </tr>
                )}
                {upcomingTasks.nodes.map((task) => (
                  <tr key={task.id}>
                    <td>{task.title}</td>
                    <td>{task.description}</td>
                    <td>{formatDate(task.startDate)}</td>
                    <td>{task.sav?.contract?.label || '-'}</td>
                    <td>
                      {task.sav?.contract?.beneficiary 
                        ? `${task.sav.contract.beneficiary.firstName} ${task.sav.contract.beneficiary.lastName}`
                        : '-'}
                    </td>
                    <td>{task.sav?.contract?.company?.name || '-'}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-link"
                        onClick={() => navigate(`/sav/${task.savId}`)}
                      >
                        SAV #{task.savId}
                      </button>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => handleTaskDone(task.id, true)}
                        >
                          Marquer comme terminée
                        </button>
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => setModalUpdateTask(task)}
                        >
                          Modifier
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination pour les tâches à venir */}
          {upcomingTasks.pageInfos.totalCount > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Page {page + 1} / {upcomingTasks.pageInfos.totalPages || 1}
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-primary"
                  disabled={!upcomingTasks.pageInfos.hasPreviousPage}
                  onClick={() => setPage(page - 1)}
                >
                  Précédent
                </button>
                <button
                  className="btn btn-outline-primary"
                  disabled={!upcomingTasks.pageInfos.hasNextPage}
                  onClick={() => setPage(page + 1)}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </Tab>
        <Tab eventKey="completed" title={`Tâches terminées (${completedTasks.pageInfos.totalCount || 0})`}>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Description</th>
                  <th>Date prévue</th>
                  <th>Date de fin</th>
                  <th>Réf. contrat</th>
                  <th>Bénéficiaire</th>
                  <th>Garant</th>
                  <th>SAV</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {completedTasks.nodes.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center">Aucune tâche terminée</td>
                  </tr>
                )}
                {completedTasks.nodes.map((task) => (
                  <tr key={task.id} className="bg-success bg-opacity-10">
                    <td>{task.title}</td>
                    <td>{task.description}</td>
                    <td>{formatDate(task.startDate)}</td>
                    <td>{task.endDate ? formatDate(task.endDate) : '-'}</td>
                    <td>{task.sav?.contract?.label || '-'}</td>
                    <td>
                      {task.sav?.contract?.beneficiary 
                        ? `${task.sav.contract.beneficiary.firstName} ${task.sav.contract.beneficiary.lastName}`
                        : '-'}
                    </td>
                    <td>{task.sav?.contract?.company?.name || '-'}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-link"
                        onClick={() => navigate(`/sav/${task.savId}`)}
                      >
                        SAV #{task.savId}
                      </button>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm btn-warning"
                          onClick={() => handleTaskDone(task.id, false)}
                        >
                          Rouvrir
                        </button>
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => setModalUpdateTask(task)}
                        >
                          Modifier
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination pour les tâches terminées */}
          {completedTasks.pageInfos.totalCount > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Page {page + 1} / {completedTasks.pageInfos.totalPages || 1}
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-primary"
                  disabled={!completedTasks.pageInfos.hasPreviousPage}
                  onClick={() => setPage(page - 1)}
                >
                  Précédent
                </button>
                <button
                  className="btn btn-outline-primary"
                  disabled={!completedTasks.pageInfos.hasNextPage}
                  onClick={() => setPage(page + 1)}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </Tab>
      </Tabs>
    </Page>
  );
}
